import {
  type ComponentType,
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { observer } from 'mobx-react'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import { v4 as uuid } from 'uuid'
import { clearSingleProduct, storeSingleProduct } from './checkoutSaver'
import useApi from '../../api/apiProvider'
import { saveAddressApi } from '../../api/address.api'
import delay from 'delay'
import useShippingOptions from './useShippingOptions'
import usePricingSummary from './usePricingSummary'
import { shippingOptionsManager } from '../../services/ShippingOptionsManager'
import { useCartStoreOrNull } from '../../stores/cartStore/CartStoreProvider'
import { calculateShippingRange } from '../../stores/shippingStore/shipping.utils'
import { SHIPPING_OPTION_FREE } from '../../stores/shippingStore/shipping.consts'
import {
  CHECKOUT_OPTION_KEY,
  countTilesByPricingProduct,
  isPricingProductsEqual,
  type PricingProduct,
  ProductIds,
} from '@mixtiles/web-backend-shared'
import { useProductType } from '../../services/ProductTypesManager'
import {
  type Address,
  type CheckoutSummary,
  type CheckoutSummaryProduct,
} from '../Pricing/types/checkout.types'
import { applyGroupShippingOptionToSummary } from '../../features/cart/groupPricingSummary'
import {
  type ShippingOption,
  type ShippingOptionName,
} from '../../stores/shippingStore/shipping.types'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { isGiftCardOrder } from '../../pages/GiftCard/utils'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import {
  getAddressStore,
  useAddressStore,
} from '../../stores/RootStoreProvider'
import { computed, toJS } from 'mobx'
import { useQueryClient } from '@tanstack/react-query'
import {
  GROUP_PRICING_QUERY_KEY,
  useGroupCheckoutPricing,
} from '../../features/cart/hooks/useGroupCheckoutPricing'
import useOnMount from 'hooks/useOnMount'
import { useLocation } from 'react-router'
import { isCartExcludedPath } from '../../features/cart/cart.consts'
import { useIsCartCheckout } from '../../features/cart/hooks/useIsCartCheckout'
import { DELIVERY_PROMISES_QUERY_KEY } from '../../features/productArrivals/deliveryPromisesQuery'
import { clearCartUpsellRecords } from 'features/cartUpsells/cartUpsellsStorage'

type CheckoutParameters = {
  orderTiles: any[]
  discountId: string | null
  products: CheckoutSummaryProduct[]
}

export type CheckoutContextValue = {
  isCheckoutOpen: boolean
  openCheckout: () => void
  closeCheckout: () => void
  checkoutParams: CheckoutParameters
  updateCheckoutParams: (params: Partial<CheckoutParameters>) => void
  updateProductQuantity: (
    productId: string,
    pricingProduct: PricingProduct,
    newQuantity: number
  ) => void
  refreshCheckoutData: () => void
  isGroupOrder: boolean
  groupCheckoutId?: string
  discountId: string | null
  orderTiles: any[]
  orderItems: any[]
  pricingSummary: CheckoutSummary
  checkoutSingleProduct: (params: {
    product: any
    shouldOpenCheckout?: boolean
  }) => void
  handleOrderComplete: (isRealOrderComplete: boolean) => void
  subscribeToPhotoOrderCompleted: (listener: () => void) => () => void
  isOrderCompleted: boolean
  setAddress: Dispatch<SetStateAction<Address>>
  setSelectedShippingOptionName: Dispatch<SetStateAction<ShippingOptionName>>
  shippingOptions: ShippingOption[]
  getDeliveryRange: (
    deliveryPromiseOption: ShippingOption | undefined
  ) => ReturnType<typeof calculateShippingRange> | null
  address: Address
  selectedShippingOption: ShippingOption | undefined
  selectedShippingOptionName: ShippingOptionName
  hasUpToDateDeliveryDates: boolean
  isCalculatingDeliveryDates: boolean
  isLoading: boolean
  error: any
  setDiscountId: (discountId: string | null) => void
  resetPricingSummaryState: () => void
  products: CheckoutSummaryProduct[]
}

type ShippingOptionChangedPayload = {
  userAddress: Address
  shippingOption: ShippingOptionName
}

const CheckoutContext = createContext<CheckoutContextValue | null>(null)
CheckoutContext.displayName = 'CheckoutContext'

const addressStateCalculator = computed(() =>
  toJS(getAddressStore().currentAddress)
)

const CheckoutProvider = observer(function CheckoutProvider({
  children,
}: PropsWithChildren) {
  const addressStore = useAddressStore()
  const api = useApi()
  const { productType } = useProductType()
  const [singleProductCheckout, setSingleProductCheckout] = useState(null)
  const [isOrderCompleted, setIsOrderCompleted] = useState<boolean>(false)
  const photoOrderCompletedListeners = useRef(new Set<() => void>())
  const subscribeToPhotoOrderCompleted = useCallback((listener: () => void) => {
    photoOrderCompletedListeners.current.add(listener)
    return () => {
      photoOrderCompletedListeners.current.delete(listener)
    }
  }, [])
  const cartStore = useCartStoreOrNull()
  const queryClient = useQueryClient()
  const addressState = addressStateCalculator.get()

  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)

  // Using a single state object for both inn order to avoid double calls to checkout endpoint when someone is doing a change
  // for both in the same time, i.e. when someone changes tileSize.
  const [checkoutParameters, setCheckoutParameters] =
    useState<CheckoutParameters>({
      orderTiles: [],
      discountId: null,
      products: [],
    })
  const [selectedShippingOptionName, setSelectedShippingOptionName] =
    useState<ShippingOptionName>(SHIPPING_OPTION_FREE)

  const previousSelectedShippingOptionName = useRef<
    ShippingOptionChangedPayload['shippingOption'] | null
  >(null)

  const { products, discountId } = checkoutParameters

  const pricingKey = isGiftCardOrder(productType)
    ? CHECKOUT_OPTION_KEY.GIFT_CARD
    : (selectedShippingOptionName as CHECKOUT_OPTION_KEY)
  const {
    pricingSummary,
    resetPricingSummaryState,
    isLoading,
    error,
    refetchPricingSummary,
  } = usePricingSummary(pricingKey, products, discountId)

  const groupPricingResult = useGroupCheckoutPricing()
  const isCartCheckout = useIsCartCheckout()
  const { pathname } = useLocation()

  // When group pricing is active (cart flow), override the pricing summary
  // with the group totals for the selected shipping option. This keeps
  // downstream components (TotalPriceSummaryItem, DeliveryPromiseItem, etc.)
  // dumb — they just render pricingSummary props as before.
  const effectivePricingSummary = useMemo((): CheckoutSummary => {
    if (!groupPricingResult.isEnabled || !groupPricingResult.data) {
      return pricingSummary
    }

    return applyGroupShippingOptionToSummary(
      pricingSummary,
      groupPricingResult.data,
      pricingKey
    )
  }, [
    pricingSummary,
    groupPricingResult.data,
    groupPricingResult.isEnabled,
    pricingKey,
  ])

  const orderItems = useMemo(() => {
    if (singleProductCheckout) {
      return [
        {
          ...pick(singleProductCheckout, [
            'itemId',
            'itemType',
            'title',
            'description',
            'totalPrice',
            'imageUrl',
            'cartItemId',
            'orderTiles',
            'tileSize',
            'materialType',
            'metadata',
          ]),
          totalPrice: isLoading
            ? null
            : {
                value: pricingSummary.productTotalPriceBeforeDiscount,
                currency: pricingSummary.currency,
              },
          priceAfterDiscount: isLoading
            ? null
            : pricingSummary.productTotalPrice,
          classicTilesPrice: isLoading
            ? null
            : pricingSummary.classicTilesPriceBeforeDiscount,
        },
      ]
    } else {
      return []
    }
  }, [singleProductCheckout, pricingSummary, isLoading])

  useEffect(() => {
    // Reset checkout params when product is changed
    updateCheckoutParams({ orderTiles: [], discountId: null, products: [] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType])

  const {
    selectedShippingOption,
    shippingOptions,
    hasUpToDateShippingOptions,
    isCalculatingShippingOptions,
  } = useShippingOptions({
    isCartCheckout,
    isCheckoutOpen,
    address: addressState,
    groupPricing: groupPricingResult.data,
    selectedShippingOptionName,
  })

  // The backend rejects a method the bag's group pricing doesn't quote (e.g. Priority for a book or
  // canvas). While a changed bag's dates load, or after they fail, the methods on screen can still
  // be the previous bag's; group pricing doesn't keep the previous bag's data, so it knows the
  // current one.
  const quotedShippingOptions = groupPricingResult.data?.shippingOptions
  const visibleShippingOptions = useMemo(
    () =>
      quotedShippingOptions
        ? shippingOptions.filter(
            (option) => option.shippingOptionName in quotedShippingOptions
          )
        : shippingOptions,
    [shippingOptions, quotedShippingOptions]
  )

  const setDiscountId = (discountId: string | null) => {
    setCheckoutParameters({
      ...checkoutParameters,
      discountId,
    })
  }

  useOnMount(() => {
    setSelectedShippingOptionName(
      shippingOptionsManager.getShippingPreference()
    )
  })

  // (silicakes) send analytics event on shipping method change
  useEffect(() => {
    if (
      previousSelectedShippingOptionName.current ===
        selectedShippingOptionName ||
      !addressState
    )
      return

    previousSelectedShippingOptionName.current = selectedShippingOptionName

    const eventPayload: ShippingOptionChangedPayload = {
      shippingOption: selectedShippingOptionName,
      userAddress: addressState,
    }

    analytics.track(EVENT_NAMES.SHIPPING_OPTION_CHANGED, eventPayload)
  }, [selectedShippingOptionName, addressState])

  useEffect(() => {
    const matchingShippingOption = visibleShippingOptions.find(
      (shippingOption: ShippingOption) =>
        shippingOption.shippingOptionName === selectedShippingOptionName
    )
    if (!matchingShippingOption) {
      setSelectedShippingOptionName(SHIPPING_OPTION_FREE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleShippingOptions])

  const openCheckout = () => {
    setIsCheckoutOpen(true)
  }

  const closeCheckout = () => setIsCheckoutOpen(false)
  /**
   * Initiate checkout for a single product
   * @param product - same structure as in CartContext.addItem
   * @param shouldOpenCheckout - determines whether it should open the checkout after setting the product
   * @param discountId
   */
  const checkoutSingleProduct = ({
    product,
    shouldOpenCheckout = true,
  }: {
    product: any
    shouldOpenCheckout?: boolean
  }) => {
    if (!product.cartItemId) {
      Object.assign(product, { cartItemId: uuid() })
    }
    setSingleProductCheckout(product)
    storeSingleProduct(product)
    if (shouldOpenCheckout) {
      delay(100).then(() => openCheckout())
    }
  }

  const handleOrderComplete = (isRealOrderComplete: boolean) => {
    clearSingleProduct()
    setSingleProductCheckout(null)
    resetPricingSummaryState()
    queryClient.removeQueries({ queryKey: [GROUP_PRICING_QUERY_KEY] })
    cartStore?.clearGroupCheckoutId()
    setCheckoutParameters({ orderTiles: [], discountId: null, products: [] })
    setIsOrderCompleted(true)
    setTimeout(() => {
      setIsOrderCompleted(false)
    }, 1000)
    if (productType !== PRODUCT_TYPES.GIFT_CARD) {
      photoOrderCompletedListeners.current.forEach((listener) => listener())
    }
    if (!isCartExcludedPath(pathname) && isRealOrderComplete) {
      cartStore?.clearAll()
      clearCartUpsellRecords()
    }
  }

  const setAddress = async (address: Address) => {
    if (!address.zipCode) {
      address.zipCode = '00000'
    }
    addressStore.setCurrentAddress(address, true)
    if (api.isAuthenticated) {
      await api.call(saveAddressApi(addressStore.currentNormalizedAddress))
    }
  }

  const updateCheckoutParams = ({
    orderTiles,
    discountId,
    products,
  }: Partial<CheckoutParameters>) => {
    const newVal = {
      ...checkoutParameters,
      ...(orderTiles ? { orderTiles } : null),
      ...(discountId !== undefined ? { discountId } : null),
      ...(products ? { products } : null),
    }
    if (orderTiles) {
      const formattedTiles = orderTiles.map((t) => {
        if (!t.customStyle) {
          return { frameColor: t.style.color, ...t }
        }
        return { ...t.customStyle, ...t }
      })
      newVal.products = countTilesByPricingProduct({
        tiles: formattedTiles,
      })
    }

    // Bail on deep-equal params — a no-op set re-renders the whole designer subtree
    setCheckoutParameters((prev) => (isEqual(newVal, prev) ? prev : newVal))
  }

  const updateProductQuantity = (
    productId: string,
    pricingProduct: PricingProduct,
    newQuantity: number
  ) => {
    const { products } = checkoutParameters
    const newProducts = [...products]
    const productIndex = newProducts.findIndex((product) =>
      product.id === ProductIds.SKU_TILES && product.pricingProduct
        ? isPricingProductsEqual(pricingProduct, product.pricingProduct)
        : productId === product.id
    )
    if (productIndex !== -1) {
      // Replace, don't mutate — the item is shared with current state
      newProducts[productIndex] = {
        ...newProducts[productIndex],
        quantity: newQuantity,
      }
      updateCheckoutParams({ products: newProducts })
    }
  }

  const getDeliveryRange = (
    deliveryPromiseOption: ShippingOption | undefined
  ) => {
    if (!deliveryPromiseOption) {
      return null
    } else {
      return calculateShippingRange(deliveryPromiseOption)
    }
  }

  const contextValue: CheckoutContextValue = {
    isCheckoutOpen,
    openCheckout,
    closeCheckout,
    checkoutParams: checkoutParameters,
    updateCheckoutParams,
    updateProductQuantity,
    refreshCheckoutData: () => {
      queryClient.resetQueries({ queryKey: [DELIVERY_PROMISES_QUERY_KEY] })
      refetchPricingSummary()
      if (isCartCheckout) {
        queryClient.resetQueries({ queryKey: [GROUP_PRICING_QUERY_KEY] })
      }
    },
    isGroupOrder: isCartCheckout,
    groupCheckoutId: groupPricingResult.data?.checkoutId,
    discountId: checkoutParameters.discountId,
    orderTiles: checkoutParameters.orderTiles,
    products: checkoutParameters.products,
    orderItems,
    pricingSummary: effectivePricingSummary,
    checkoutSingleProduct,
    handleOrderComplete,
    subscribeToPhotoOrderCompleted,
    isOrderCompleted,
    setAddress,
    setSelectedShippingOptionName,
    shippingOptions: visibleShippingOptions,
    getDeliveryRange,
    address: addressState,
    selectedShippingOption,
    selectedShippingOptionName,
    hasUpToDateDeliveryDates: hasUpToDateShippingOptions,
    isCalculatingDeliveryDates: isCalculatingShippingOptions,
    // While a promo code reloads the discounts, group pricing is disabled on a new key and counts
    // as pending, so pay waits for it instead of the order changing shape.
    isLoading:
      isLoading ||
      (isCartCheckout &&
        (groupPricingResult.isPending || groupPricingResult.isFetching)),
    error: error ?? (isCartCheckout ? groupPricingResult.error : null),
    setDiscountId,
    resetPricingSummaryState,
  }

  return (
    <CheckoutContext.Provider value={contextValue}>
      {children}
    </CheckoutContext.Provider>
  )
})

function useCheckout() {
  const context = useContext(CheckoutContext)

  if (!context) {
    throw new Error('useCheckout must be used within a CheckoutProvider')
  }

  return context
}

function withCheckout<P extends { checkout?: CheckoutContextValue }>(
  Component: ComponentType<P>
) {
  return function CheckoutComp(props: P) {
    return (
      <CheckoutContext.Consumer>
        {(contexts) => <Component {...props} checkout={contexts} />}
      </CheckoutContext.Consumer>
    )
  }
}

export { CheckoutProvider, useCheckout, withCheckout }
